/**
 * CR108: "What is this?" explainer for Rule Packs
 * Helps users understand the difference between Rule Packs, Prompt Packs, Deal Cards, and Theme Labs
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, HelpCircle, ExternalLink, FileJson, MessageSquare, LayoutGrid, Palette } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface RulePacksExplainerProps {
  defaultOpen?: boolean;
}

const ROUTING_TABLE = [
  {
    intent: "Change dedupe thresholds, determinism rules, hybrid AI mode",
    destination: "Rule Packs",
    icon: FileJson,
    badge: "json-config",
    path: null, // Already here
  },
  {
    intent: "Edit normalize prompt wording / schema instructions",
    destination: "Prompt Packs",
    icon: MessageSquare,
    badge: "prompts",
    path: "/admin/prompt-packs",
  },
  {
    intent: "Change card layout, sections, enable/disable elements",
    destination: "Deal Cards",
    icon: LayoutGrid,
    badge: "per-type",
    path: "/admin/command-center",
  },
  {
    intent: "Change global palette, typography, pill styles, ribbon tokens",
    destination: "Theme Labs",
    icon: Palette,
    badge: "global",
    path: "/admin/command-center",
  },
];

export function RulePacksExplainer({ defaultOpen = false }: RulePacksExplainerProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const navigate = useNavigate();

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border rounded-lg bg-muted/20">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-4 h-auto hover:bg-muted/40">
          <div className="flex items-center gap-2 text-left">
            <HelpCircle className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">What is this? Where do I edit what?</span>
          </div>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 space-y-4">
        {/* Quick definitions */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="p-3 rounded-lg border bg-card">
            <div className="flex items-center gap-2 mb-1">
              <FileJson className="h-4 w-4 text-purple-500" />
              <span className="font-semibold">Rule Pack</span>
              <Badge variant="outline" className="text-[10px] py-0">JSON config</Badge>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Deterministic configuration (thresholds, field precedence, source priority). 
              <strong className="text-foreground"> NOT for AI prompts.</strong>
            </p>
            <pre className="mt-2 p-2 bg-muted rounded text-[10px] overflow-x-auto">
{`{
  "mode": "deterministic",
  "similarity_threshold": 0.85
}`}
            </pre>
          </div>
          <div className="p-3 rounded-lg border bg-card">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="h-4 w-4 text-orange-500" />
              <span className="font-semibold">Prompt Pack</span>
              <Badge variant="outline" className="text-[10px] py-0">text prompts</Badge>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Actual AI prompts with instructions, role definitions, and output contracts.
              <strong className="text-foreground"> For AI behavior tuning.</strong>
            </p>
            <pre className="mt-2 p-2 bg-muted rounded text-[10px] overflow-x-auto">
{`You are a deduplication assistant.
OUTPUT CONTRACT: Return JSON...`}
            </pre>
          </div>
        </div>

        {/* Routing table */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs font-semibold">I want to...</TableHead>
                <TableHead className="text-xs font-semibold w-32">Go to...</TableHead>
                <TableHead className="text-xs font-semibold w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ROUTING_TABLE.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell className="text-xs py-2">{row.intent}</TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1.5">
                      <row.icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium">{row.destination}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    {row.path ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => navigate(row.path!)}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Go
                      </Button>
                    ) : (
                      <Badge variant="secondary" className="text-[10px] py-0">
                        You're here
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <p className="text-xs text-muted-foreground">
          💡 <strong>Tip:</strong> If you're pasting text that starts with "You are..." or contains "OUTPUT CONTRACT", 
          that's a prompt—head to <strong>Prompt Packs</strong> instead.
        </p>
      </CollapsibleContent>
    </Collapsible>
  );
}
